import React from 'react';
import { Stack, useMediaQuery, useTheme } from '@mui/material';
import PreviewBoardPlaceholder from '../common/PreviewBoardPlaceholder';
import CoursePreviewLineBoard from './CoursePreviewLineBoard';
import {
  COURSE_PREVIEW_BOARD_COUNT,
  COURSE_PREVIEW_BOARD_STAGGER_MS,
  resolveCoursePreviewBoardWidth,
} from './coursePreviewBoardSizes';
import type { CoursePreviewThumbnail, TrainSide } from './types';

type CoursePreviewBoardsProps = {
  previews?: CoursePreviewThumbnail[];
  trainSide?: TrainSide;
  boardWidth?: number;
  boardCount?: number;
  /** When false, render placeholders until the row is near the viewport. */
  mountBoards?: boolean;
  /** When false, pause preview autoplay (tab hidden or row off-screen). */
  animationsActive?: boolean;
  /** Show the start position only — no line animation on any device. */
  staticPreviews?: boolean;
};

const CoursePreviewBoards = ({
  previews,
  trainSide,
  boardWidth: boardWidthProp,
  boardCount = COURSE_PREVIEW_BOARD_COUNT,
  mountBoards = true,
  animationsActive = true,
  staticPreviews = false,
}: CoursePreviewBoardsProps) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const isUltraWide = useMediaQuery(theme.breakpoints.up('xxxl'));
  const isWideDesktop = useMediaQuery(theme.breakpoints.up('xxl'));
  const isTouchBrowse = useMediaQuery('(hover: none) and (pointer: coarse)');
  const boardWidth =
    boardWidthProp ??
    resolveCoursePreviewBoardWidth({
      isMobile,
      isWideDesktop,
      isUltraWide,
    });
  // Coarse-pointer media queries are false in Chrome DevTools device mode; treat
  // narrow viewports as touch browse so card previews stay static while testing.
  const previewAutoplay =
    !staticPreviews && animationsActive && !(isTouchBrowse || isMobile);

  return (
    <Stack
      direction="row"
      spacing={{ xs: 0.75, sm: 1, xxl: 1.25, xxxl: 1.5 }}
      aria-label="Course position previews"
    >
      {Array.from({ length: boardCount }, (_, index) => {
        if (!mountBoards) {
          return <PreviewBoardPlaceholder key={index} boardWidth={boardWidth} />;
        }

        const preview = previews?.[index];
        return (
          <CoursePreviewLineBoard
            key={index}
            pgn={preview?.pgn}
            startFen={preview?.startFen}
            setupUci={preview?.setupUci}
            trainSide={preview?.trainSide ?? trainSide}
            boardWidth={boardWidth}
            startDelayMs={index * COURSE_PREVIEW_BOARD_STAGGER_MS}
            animate={previewAutoplay}
            autoplay={previewAutoplay}
          />
        );
      })}
    </Stack>
  );
};

export default CoursePreviewBoards;
